import type {
  CheckClipInfo,
  CheckVerdict,
  ClientConfig,
  Audience,
  LiveKind,
  LiveSourceDescriptor,
  LiveTierName,
  MuteState,
  ParticipantStatus,
  PeerInfo,
  RecordingState,
  Role,
  RoomConfig,
  RoomView,
  RosterEntry,
  ServerMessage,
  StageLayout,
  StudioSettings,
  SourceKind,
  StudioInfo,
  TimelineEvent,
} from '../../../shared/protocol.ts';
import { api } from './api.ts';
import { ServerClock } from './clock.ts';
import { fallbackProfile, probeHardware, withTimeout, type HardwareProfile } from './capability.ts';
import { capacityFor, type QualityMode } from './quality.ts';
import { listLocalTakes, saveTakeToFolder } from './local-copies.ts';
import { saveFolder } from './save-folder.ts';
import { installTrackProcessor, pipelineStats, unlockAudio, warmAudioTaps } from './track-processor.ts';
import { startDiagUpload } from './diag.ts';
import { Observable, localNow, sleep, uid } from './emitter.ts';
import { BandwidthGovernor } from './governor.ts';
import { Mesh } from './mesh.ts';
import { SourceRecorder, type TailClip } from './recorder.ts';
import { Signaling } from './signaling.ts';
import { loadPrefs, muteState, SourceManager, type CapturedSource } from './sources.ts';
import { isJournalName, TakeJournal } from './journal.ts';
import { localFiles, storageEstimate } from './storage.ts';
import { Uploader } from './uploader.ts';

const PARTICIPANT_KEY = 'takehouse.participantId';
const TAB_PARTICIPANT_KEY = 'takehouse.tabParticipantId';
/** A recorder that fails this often in a minute is given up on for the rest of the take (see recoverRecorder). */
const MAX_RECORDER_FAILURES = 3;
/** Wait before the 1st, 2nd… replacement file: the first straight away, so a one-off crash loses nothing. */
const RECORDER_RETRY_WAIT_MS = [0, 2000];

/**
 * Who this tab is in the studio. The browser keeps one identity, so a tab reopened after a crash carries on as
 * the same person. A second tab open at the same time must not share it: the server would see one person twice
 * and both tabs would record under the same name (one of them with a camera Safari has switched off). A Web Lock
 * held for the life of the tab tells us whether a live tab already has the identity.
 */
export async function claimParticipantId(): Promise<{ id: string; otherTab: boolean }> {
  let main = localStorage.getItem(PARTICIPANT_KEY);
  if (!main) localStorage.setItem(PARTICIPANT_KEY, (main = crypto.randomUUID()));
  if (!navigator.locks) return { id: main, otherTab: false };
  // A reload keeps this tab's identity (the old page's lock may take a moment to be released).
  const previous = sessionStorage.getItem(TAB_PARTICIPANT_KEY);
  let id = previous ?? main;
  if (!(await holdIdentity(id, 1500))) {
    id = crypto.randomUUID();
    await holdIdentity(id, 0);
  }
  sessionStorage.setItem(TAB_PARTICIPANT_KEY, id);
  return { id, otherTab: id !== main };
}

function holdIdentity(id: string, waitMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const options: LockOptions = waitMs ? { signal: AbortSignal.timeout(waitMs) } : { ifAvailable: true };
    navigator.locks
      .request(`takehouse:participant:${id}`, options, (lock) => {
        resolve(!!lock);
        // Held until the tab closes.
        return lock ? new Promise(() => {}) : undefined;
      })
      .catch(() => resolve(false));
  });
}

interface ActiveRecorder {
  recorder: SourceRecorder;
  source: CapturedSource;
  localName: string;
  trackId: string | null;
  startedServerMs: number;
}

export interface CheckRun {
  id: string;
  reason: 'self' | 'host' | 'preflight';
  requestedBy?: string;
  checkId?: string;
  status: 'capturing' | 'ready' | 'failed';
  clips: TailClip[];
  message?: string;
  countdown?: number;
  /** A pre-flight test where the video encoders couldn't keep up with the plan. */
  struggled?: boolean;
}

/** Shared singletons that outlive a single studio visit (uploads continue across pages). */
let shared: { governor: BandwidthGovernor; uploader: Uploader } | null = null;
export function sharedUploads() {
  if (!shared) {
    const governor = new BandwidthGovernor();
    shared = { governor, uploader: new Uploader(localFiles(), governor) };
  }
  return shared;
}

export class StudioEngine extends Observable {
  readonly studioId: string;
  readonly key: string;
  participantId = '';
  /** Another tab of this browser is in the studio too, so this one joined as a separate person. */
  otherTab = false;
  name = '';
  role: Role = 'guest';
  studio: StudioInfo | null = null;
  selfId = '';
  joined = false;
  connected = false;
  peers = new Map<string, PeerInfo>();
  recording: RecordingState | null = null;
  /** In the lobby, waiting for a host or producer to let this person in. */
  waiting = false;
  /** The room this person is in (main studio or a green room), with its layout and whether it records. */
  room: RoomView | null = null;
  /** Hosts and producers: everyone, waiting or in a room, and the settings they direct. */
  roster: RosterEntry[] = [];
  settings: StudioSettings | null = null;
  /** Sends the diagnostic log to the server now (it also goes every 15 s). */
  flushDiag: () => void = () => {};
  /** The take that just ended, so the room can point the host at its files. */
  lastTake: { takeId: string; takeNumber: number; localOnly?: boolean } | null = null;
  /** Copying the last take into the save folder (save-folder.ts), if one is set. */
  lastTakeSave: { state: 'saving' | 'saved' | 'needs-permission' | 'failed'; folder?: string; error?: string } | null = null;
  hardware: HardwareProfile | null = null;
  sources: SourceManager;
  governor: BandwidthGovernor;
  uploader: Uploader;
  signaling: Signaling | null = null;
  mesh: Mesh | null = null;
  config: ClientConfig | null = null;
  recorders = new Map<string, ActiveRecorder>();
  /** This computer's own record of the current take (see journal.ts). */
  journal: TakeJournal | null = null;
  /** Timeline events raised while the server couldn't be reached, sent when it can. */
  private outbox: TimelineEvent[] = [];
  private offlineSince: number | null = null;
  /**
   * How hard this computer is working to keep the recording smooth (0 = normal, 3 = most protection).
   * The order things give way is fixed: uploads and the live video first, then the screen recording's frame
   * rate, then the camera recording's. Audio, recorded or live, is never reduced.
   */
  loadLevel: LoadLevel = 0;
  private forcedLoad: LoadLevel | null = null;
  private backlogSamples: number[] = [];
  private lastLevelChange = 0;
  private calmSince = 0;
  /** Consecutive 2-s readings showing strain: one blip (an encoder warming up) is not a trend. */
  private strainStreak = 0;
  private stressStreak = 0;
  private lastRecorderStart = 0;
  private droppedSeen = 0;
  private loadTimer?: number;
  private progress = new Map<SourceRecorder, { media: number; at: number; flagged?: boolean }>();
  private lastStallRestart = 0;
  /** Recent recorder failures per source; a source that keeps failing is given up on for the rest of the take. */
  private failures = new Map<string, number[]>();
  private givenUp = new Set<string>();
  private lastDiagFlush = 0;
  check: CheckRun | null = null;
  checkClips: CheckClipInfo[] = [];
  cpuPressure: ParticipantStatus['cpuPressure'] = 'unknown';
  storageFree: number | undefined;
  warnings: string[] = [];
  fatal: string | null = null;
  private statusTimer?: number;
  private wakeLock: any = null;
  private readonly storage = localFiles();

  constructor(studioId: string, key: string) {
    super();
    this.studioId = studioId;
    this.key = key;
    this.sources = new SourceManager();
    const s = sharedUploads();
    this.governor = s.governor;
    this.uploader = s.uploader;
    this.sources.subscribe(() => this.onSourcesChanged());
    this.sources.onSourceEnded((src) => void this.onSourceEnded(src));
    this.governor.subscribe(() => this.changed());
    this.uploader.subscribe(() => this.changed());
  }

  /** Validates the link, measures the machine and opens camera + mic for the green room. */
  async init() {
    addEventListener('takehouse:mic-silent', () =>
      this.warn('Your microphone is sending pure silence. Check it isn’t muted in system settings, reselect it, or reload the page.'),
    );
    const me = await claimParticipantId();
    this.participantId = me.id;
    this.otherTab = me.otherTab;
    try {
      this.studio = await api.studio(this.studioId, this.key);
      this.role = this.studio.role;
      this.flushDiag = startDiagUpload(this.studioId, this.key, () => ({ participantId: this.participantId, name: this.name || `(${this.role})` }));
    } catch (e) {
      this.fatal = (e as Error).message || 'This studio link is not valid.';
      this.changed();
      return;
    }
    if (this.otherTab) console.warn('[takehouse] another tab of this browser is already in Wordstow; this tab joins as a separate person');
    this.config = await api.config(this.studioId, this.key);
    await this.storage.ready;
    this.hardware = await probeHardware().catch((e) => {
      console.error('[takehouse] hardware check failed', e);
      return fallbackProfile();
    });
    this.sources.capacity = capacityFor(this.hardware);
    const hw = this.hardware;
    console.info(
      `[takehouse] hardware: tier ${hw.tier}, ${hw.cores} cores, video ${hw.videoCodec} (${hw.videoLatencyMode}), audio ${hw.audioCodec}, 720p encode ${hw.encodeFps720 ?? '?'} fps`,
    );
    let force = false;
    try {
      force = localStorage.getItem('takehouse.forceTrackProcessor') === '1';
    } catch {}
    await withTimeout('track processor setup', installTrackProcessor(force), 5000)
      .then((how) => console.info(`[takehouse] track processor: ${how}`))
      .catch((e) => console.error('[takehouse] track processor setup failed', e));
    this.changed();
    const prefs = loadPrefs();
    await this.sources.addCamera(prefs.camera).then((s) => s ?? this.sources.addCamera());
    await this.sources.addMicrophone(prefs.microphone).then((s) => s ?? this.sources.addMicrophone());
    await this.refreshStorage();
    this.watchCpu();
  }

  setQualityMode(mode: QualityMode) {
    this.sources.setMode(mode);
    this.changed();
  }

  join(name: string) {
    unlockAudio(); // this runs inside the Join click, which is what lets Safari start audio
    this.name = name.trim() || 'Guest';
    localStorage.setItem('takehouse.name', this.name);
    this.joined = true;
    warmAudioTaps(this.sources.active.filter((s) => s.kind === 'microphone').map((s) => s.track));
    const signaling = new Signaling(this.studioId, this.key, () => ({ type: 'hello', name: this.name, participantId: this.participantId }));
    this.signaling = signaling;
    this.mesh = new Mesh(signaling, this.config?.iceServers ?? []);
    this.mesh.onHealth = (h) => this.governor.update(h);
    this.mesh.subscribe(() => {
      this.sources.setLiveReceivers([...this.mesh!.peers.values()].filter((p) => p.state === 'connected').length);
      this.changed();
    });
    signaling.on('message', (m) => this.onMessage(m));
    signaling.on('open', () => {
      this.connected = true;
      this.publishLiveSources();
      if (this.offlineSince != null && this.recording) {
        const secs = Math.round((localNow() - this.offlineSince) / 1000);
        this.logEvent('reconnected', `Back online after ${secs} s; local recording carried on throughout`);
      }
      this.offlineSince = null;
      for (const event of this.outbox.splice(0)) signaling.send({ type: 'event', event });
      this.changed();
    });
    signaling.on('close', ({ willRetry, code }) => {
      this.connected = false;
      this.offlineSince ??= localNow();
      if (!willRetry)
        this.fatal =
          code === 4404 ? 'This studio has been deleted by its host.' : code === 4403 ? 'The host didn’t let you in this time.' : 'Disconnected from the studio.';
      // Peers will re-announce; old connections are useless once ids change.
      for (const id of [...this.peers.keys()]) this.mesh?.removePeer(id);
      this.peers.clear();
      this.changed();
    });
    this.statusTimer = window.setInterval(() => this.publishStatus(), 2000);
    // How far behind the video encoders are, sampled often enough to catch a struggling computer quickly.
    this.loadTimer = window.setInterval(() => {
      let worst = 0;
      for (const r of this.recorders.values()) if (r.recorder.ext === 'mp4') worst = Math.max(worst, r.recorder.encoderBacklog);
      this.backlogSamples.push(worst);
    }, 500);
    addEventListener('beforeunload', this.beforeUnload);
    // The screen wake lock lapses whenever the tab is hidden; take it again on return during a take.
    document.addEventListener('visibilitychange', this.onVisibility);
    this.pushLocalTracks();
    this.changed();
  }

  private beforeUnload = (e: BeforeUnloadEvent) => {
    if (this.recording || this.uploader.pending.length) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  private onVisibility = async () => {
    if (document.visibilityState !== 'visible' || !this.recording || (this.wakeLock && !this.wakeLock.released)) return;
    try {
      this.wakeLock = await (navigator as any).wakeLock?.request('screen');
    } catch {}
  };

  async leave() {
    if (this.recording) await this.endTake('left');
    this.signaling?.close();
    this.mesh?.close();
    clearInterval(this.statusTimer);
    clearInterval(this.loadTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.sources.stopAll();
    removeEventListener('beforeunload', this.beforeUnload);
    this.joined = false;
    this.changed();
  }

  // --- messages -------------------------------------------------------------

  private onMessage(m: ServerMessage) {
    switch (m.type) {
      case 'welcome':
        this.waiting = false;
        this.selfId = m.selfId;
        this.role = m.role;
        this.studio = m.studio;
        this.room = m.room;
        this.mesh!.setSelf(m.selfId);
        this.peers = new Map(m.peers.map((p) => [p.id, p]));
        for (const p of m.peers) this.mesh!.addPeer(p);
        void this.onRecordingState(m.recording);
        break;
      case 'waiting':
        this.waiting = true;
        break;
      case 'room':
        // Moved to another room: a fresh set of people to connect to; recording follows the room's setting.
        for (const id of [...this.peers.keys()]) this.mesh?.removePeer(id);
        this.peers = new Map(m.peers.map((p) => [p.id, p]));
        for (const p of m.peers) this.mesh?.addPeer(p);
        this.room = m.room;
        this.logEvent('warning', `Moved to ${m.room.name}${m.room.record ? '' : ' (not recorded there)'}`);
        if (this.recording) void this.syncRecorders();
        break;
      case 'room-config': {
        const recordChanged = this.room?.record !== m.room.record;
        this.room = m.room;
        if (recordChanged && this.recording) void this.syncRecorders();
        break;
      }
      case 'send-rules':
        this.mesh?.setSendRules(m.rules);
        break;
      case 'roster':
        this.roster = m.entries;
        this.settings = m.settings;
        break;
      case 'peer-joined':
        this.peers.set(m.peer.id, m.peer);
        this.mesh?.addPeer(m.peer);
        if (this.requestedTier !== 'high') this.signaling?.send({ type: 'prefer-tier', to: m.peer.id, tier: this.requestedTier });
        break;
      case 'peer-left':
        this.peers.delete(m.peerId);
        this.mesh?.removePeer(m.peerId);
        break;
      case 'peer-status': {
        const p = this.peers.get(m.peerId);
        if (p) this.peers.set(m.peerId, { ...p, status: m.status });
        break;
      }
      case 'peer-live-sources':
        this.mesh?.setPeerLiveSources(m.peerId, m.sources);
        break;
      case 'signal':
        void this.mesh?.handleSignal(m.from, m.data);
        break;
      case 'recording':
        void this.onRecordingState(m.state);
        break;
      case 'check-requested':
        void this.runCheck('host', { checkId: m.checkId, requestedBy: m.requestedBy });
        break;
      case 'check-clip':
        this.checkClips = [m.clip, ...this.checkClips].slice(0, 60);
        break;
      case 'prefer-tier':
        this.mesh?.setPreferredTier(m.from, m.tier);
        break;
      case 'error':
        this.fatal = m.message;
        break;
    }
    this.changed();
  }

  // --- live -----------------------------------------------------------------

  private onSourcesChanged() {
    // Safari path: once joined (a click has started audio), keep each microphone's shared audio input open,
    // so recordings, often started by the host's command, start on a running input.
    if (this.joined) warmAudioTaps(this.sources.active.filter((s) => s.kind === 'microphone').map((s) => s.track));
    this.pushLocalTracks();
    this.publishLiveSources();
    if (this.recording) void this.syncRecorders();
    this.changed();
  }

  private pushLocalTracks() {
    if (!this.mesh) return;
    const cam = this.sources.primaryCamera;
    const mic = this.sources.primaryMic;
    const screen = this.sources.active.find((s) => s.kind === 'screen' && s.use.live);
    this.mesh.setLocal({
      camera: cam && muteState(cam) === 'none' ? cam.track : null,
      mic: mic && muteState(mic) === 'none' ? mic.track : null,
      screen: screen && muteState(screen) === 'none' ? screen.track : null,
      screenAudio: (() => {
        const sound = this.sources.soundOf(screen);
        return screen && muteState(screen) === 'none' && sound?.use.live && muteState(sound) === 'none' ? sound.track : null;
      })(),
    });
  }

  private publishLiveSources() {
    // Everyone sees each other's mute state, so a muted tile is never mistaken for a broken one.
    const live: LiveSourceDescriptor[] = [];
    const cam = this.sources.primaryCamera;
    const mic = this.sources.primaryMic;
    const screen = this.sources.active.find((s) => s.kind === 'screen' && s.use.live);
    if (cam) live.push({ streamId: cam.id, kind: 'camera', label: cam.label, mute: muteState(cam) });
    if (mic) live.push({ streamId: mic.id, kind: 'microphone', label: mic.label, mute: muteState(mic) });
    if (screen && muteState(screen) === 'none') live.push({ streamId: screen.id, kind: 'screen', label: screen.label, mute: 'none' });
    this.signaling?.send({ type: 'live-sources', sources: live });
  }

  /**
   * Two levels of mute:
   *  - 'send': not sent to the call, still recorded locally (and marked on the timeline)
   *  - 'hard': not sent and not recorded – the file gets silence / black so it stays in sync
   */
  setMute(sourceId: string, state: MuteState) {
    const s = this.sources.byId(sourceId);
    if (!s) return;
    const before = muteState(s);
    if (before === state) return;
    this.sources.setHardMuted(sourceId, state === 'hard');
    this.sources.setLiveMuted(sourceId, state !== 'none');
    if (state === 'hard') this.logEvent('hard-muted', `${s.label}: hard mute – not sent, not recorded (silence/black written)`);
    else if (before === 'hard') this.logEvent('hard-unmuted', `${s.label}: recording resumed${state === 'send' ? ' (still not sent to the call)' : ''}`);
    if (state === 'send' && before === 'none') this.logEvent('muted', `${s.label}: not sent to the call (still recorded)`);
    if (state === 'none') this.logEvent('unmuted', `${s.label}: back on the call`);
  }

  /** Main mute button: live ↔ send-muted; from hard mute it goes straight back to live. */
  toggleSendMute(sourceId: string) {
    const s = this.sources.byId(sourceId);
    if (s) this.setMute(sourceId, muteState(s) === 'none' ? 'send' : 'none');
  }

  /** Hard-mute button: on → hard; off → back to send-muted, so going live again is a deliberate second step. */
  toggleHardMute(sourceId: string) {
    const s = this.sources.byId(sourceId);
    if (s) this.setMute(sourceId, muteState(s) === 'hard' ? 'send' : 'hard');
  }

  // --- directing (hosts and producers) ---------------------------------------------------------------

  admit(id: string, roomId?: string) {
    this.signaling?.send({ type: 'admit', id, roomId });
  }
  turnAway(id: string) {
    this.signaling?.send({ type: 'deny', id });
  }
  moveTo(id: string, roomId: string) {
    this.signaling?.send({ type: 'move', id, roomId });
  }
  /** Changes the studio's settings for everyone; the server checks them and everyone follows. */
  updateSettings(change: (s: StudioSettings) => StudioSettings) {
    if (!this.settings) return;
    const next = change(structuredClone(this.settings));
    this.settings = next; // shown at once; the server's confirmed copy follows in the roster
    this.signaling?.send({ type: 'settings', settings: next });
    this.changed();
  }
  updateRoom(roomId: string, change: (r: RoomConfig) => void) {
    this.updateSettings((s) => {
      const r = s.rooms.find((x) => x.id === roomId);
      if (r) change(r);
      return s;
    });
  }
  setLayout(layout: StageLayout) {
    if (this.room) {
      this.room = { ...this.room, layout };
      this.updateRoom(this.room.id, (r) => (r.layout = layout));
    }
  }
  setAudience(participantId: string, kind: LiveKind, audience: Audience) {
    if (!this.room) return;
    this.updateRoom(this.room.id, (r) => {
      const entry = { ...(r.audience[participantId] ?? {}) };
      if (audience === 'everyone') delete entry[kind];
      else entry[kind] = audience;
      if (Object.keys(entry).length) r.audience[participantId] = entry;
      else delete r.audience[participantId];
    });
  }

  private liveCap(): LiveTierName {
    let cap: LiveTierName = this.hardware?.tier === 'light' ? 'medium' : 'high';
    if (this.cpuPressure === 'serious' || this.loadLevel === 1) cap = 'medium';
    if (this.cpuPressure === 'critical' || this.loadLevel >= 2) cap = 'low';
    return cap;
  }

  /**
   * What we ask the others to send us. Decoding their video costs CPU, so a struggling computer asks for less,
   * and asks for full quality again once it recovers (otherwise their video would stay low for the session).
   */
  private requestedTier: LiveTierName = 'high';
  private updatePeerRequest(force = false) {
    let tier: LiveTierName = 'high';
    if (this.cpuPressure === 'serious' || this.loadLevel === 1) tier = 'medium';
    if (this.cpuPressure === 'critical' || this.loadLevel >= 2) tier = 'low';
    if (tier === this.requestedTier && !force) return;
    this.requestedTier = tier;
    for (const id of this.peers.keys()) this.signaling?.send({ type: 'prefer-tier', to: id, tier });
  }

  /** Test and support hook: hold the protection ladder at a level (null = back to automatic). */
  simulateLoad(level: LoadLevel | null) {
    this.forcedLoad = level;
    this.evaluateLoad();
  }

  /**
   * Every 2 s: is this computer keeping up? Signals are the system's CPU pressure (Chrome reports it for the whole
   * machine, so easing off also helps the person's other apps) and how far behind our video encoders are.
   * Steps up one level at a time (at most every 4 s) and back down only after 20 s of calm.
   */
  private evaluateLoad() {
    const now = localNow();
    // Encoders queue frames while they warm up; readings from a recorder's first seconds say nothing.
    const samples = now - this.lastRecorderStart < 5000 ? (this.backlogSamples.splice(0), []) : this.backlogSamples.splice(0);
    const avg = samples.length ? samples.reduce((a, b) => a + b, 0) / samples.length : 0;
    const peak = samples.length ? Math.max(...samples) : 0;
    // Frames the Safari-path pipeline threw away because the recorder was behind (before the encoder sees them).
    const dropped = pipelineStats.droppedVideoFrames - this.droppedSeen;
    this.droppedSeen = pipelineStats.droppedVideoFrames;
    const stressNow = this.cpuPressure === 'critical' || avg >= 4 || peak >= 7 || dropped >= 15;
    const strainNow = stressNow || this.cpuPressure === 'serious' || avg >= 2 || dropped >= 3;
    this.stressStreak = stressNow ? this.stressStreak + 1 : 0;
    this.strainStreak = strainNow ? this.strainStreak + 1 : 0;
    const stress = this.stressStreak >= 2;
    const strain = this.strainStreak >= 2;
    let level = this.loadLevel;
    if (this.forcedLoad != null) level = this.forcedLoad;
    else if (!this.recorders.size) level = 0;
    else if (stress && now - this.lastLevelChange > 4000) level = Math.min(3, level + 1) as LoadLevel;
    else if (strain && level === 0) level = 1;
    if (strainNow || this.forcedLoad != null) this.calmSince = 0;
    else {
      this.calmSince ||= now;
      if (level > 0 && now - this.calmSince > 20_000 && now - this.lastLevelChange > 20_000) level = (level - 1) as LoadLevel;
    }
    if (level !== this.loadLevel) this.applyLoadLevel(level, `encoder backlog ${avg.toFixed(1)} (peak ${peak}), ${dropped} frames dropped, CPU ${this.cpuPressure}`);
    this.checkProgress(now);
  }

  private applyLoadLevel(level: LoadLevel, why: string) {
    const before = this.loadLevel;
    this.loadLevel = level;
    this.lastLevelChange = localNow();
    this.governor.setPressureHold(level >= 1);
    for (const r of this.recorders.values()) r.recorder.frameDivisor = frameDivisorFor(r.source.kind, level);
    this.mesh?.setLocalCap(this.liveCap());
    this.updatePeerRequest();
    const text = LOAD_TEXT[level];
    console.info(`[takehouse] protection level ${before} → ${level} (${why}): ${text}`);
    this.logEvent(level > before ? 'quality-reduced' : 'quality-restored', text);
    if (level >= 2 && before < 2) this.warn(`This computer is struggling to keep up. To protect the recording: ${LOAD_TEXT[level].toLowerCase()}. Closing other apps will help.`);
    this.changed();
  }

  /**
   * A recording whose media time stops moving has stalled. A stalled microphone gets a fresh file (once every
   * 30 s at most); a camera that stops delivering frames is reported, since only the person can fix that.
   * Screens are left alone: a still screen legitimately sends no new frames.
   */
  private checkProgress(now: number) {
    for (const r of this.recorders.values()) {
      const rec = r.recorder;
      if (rec.state !== 'recording' || !r.startedServerMs || r.source.kind === 'screen') continue;
      const p = this.progress.get(rec);
      if (!p || rec.lastMediaTime > p.media) {
        this.progress.set(rec, { media: rec.lastMediaTime, at: now });
        continue;
      }
      if (now - p.at < 6000 || p.flagged) continue;
      p.flagged = true;
      if (r.source.kind === 'microphone' && now - this.lastStallRestart > 30_000) {
        this.lastStallRestart = now;
        void this.recoverRecorder(r, new Error('no audio arrived for 6 s'));
      } else if (r.source.kind === 'camera') {
        const msg = `${r.source.label} has stopped sending video, so the recording is frozen. Check nothing else is using the camera, or reselect it.`;
        this.warn(msg);
        this.logEvent('warning', msg, r.trackId ?? undefined);
      }
    }
  }

  private watchCpu() {
    const PO = (globalThis as any).PressureObserver;
    if (!PO) return;
    try {
      const obs = new PO((records: any[]) => {
        const state = records.at(-1)?.state;
        if (state && state !== this.cpuPressure) {
          this.cpuPressure = state;
          this.mesh?.setLocalCap(this.liveCap());
          this.updatePeerRequest();
          this.changed();
        }
      });
      void obs.observe('cpu', { sampleInterval: 2000 });
    } catch {}
  }

  // --- recording --------------------------------------------------------------

  startRecording() {
    this.signaling?.send({ type: 'start-recording' });
  }
  stopRecording() {
    this.signaling?.send({ type: 'stop-recording' });
  }
  requestCheck() {
    this.signaling?.send({ type: 'request-check' });
  }
  addMarker(label = 'Marker') {
    this.logEvent('marker', label);
  }

  private async onRecordingState(state: RecordingState | null) {
    if (state && this.recording?.takeId === state.takeId) return;
    if (!state && this.recording) {
      await this.endTake('stopped');
      return;
    }
    if (state && this.recording) await this.endTake('stopped');
    if (state) await this.beginTake(state);
  }

  private async beginTake(state: RecordingState) {
    this.recording = state;
    this.failures.clear();
    this.givenUp.clear();
    this.governor.setRecording(true);
    this.changed();
    await this.openJournal(state);
    try {
      this.wakeLock = await (navigator as any).wakeLock?.request('screen');
    } catch {}
    await this.syncRecorders(state.startAt);
  }

  /** The take's journal, continued if this tab already kept one for it (a rejoin after a crash or reload). */
  private async openJournal(state: RecordingState) {
    const fresh = new TakeJournal(this.storage, {
      studioId: this.studioId,
      studioName: this.studio?.name ?? '',
      key: this.key,
      takeId: state.takeId,
      takeNumber: state.takeNumber,
      takeStartServerMs: state.startAt,
      participantId: this.participantId,
      participantName: this.name,
      role: this.role,
    });
    const onDisk = await this.storage.list().then((l) => l.some((f) => f.name === fresh.name && isJournalName(f.name)), () => false);
    this.journal = (onDisk && (await TakeJournal.resume(this.storage, fresh.name))) || fresh;
    await this.journal.saveNow();
    this.journal.startUploading();
  }

  /** Make the set of running recorders match the sources marked "record". */
  private syncing: Promise<void> = Promise.resolve();
  private syncRecorders(startAtServer?: number) {
    this.syncing = this.syncing.then(() => this.doSyncRecorders(startAtServer)).catch((e) => console.error(e));
    return this.syncing;
  }

  private async doSyncRecorders(startAtServer?: number) {
    const take = this.recording;
    if (!take || !this.hardware) return;
    // Only rooms set to record are recorded (the main studio by default; green rooms if the host says so).
    const wanted = this.room?.record === false ? [] : this.sources.active.filter((s) => s.use.record && !this.givenUp.has(s.id));
    for (const [id, r] of this.recorders) {
      if (!wanted.find((s) => s.id === id)) await this.stopRecorder(r, 'stopped');
    }
    const fresh: ActiveRecorder[] = [];
    for (const source of wanted) {
      if (this.recorders.has(source.id)) continue;
      const r = await this.createRecorder(source, take.takeId);
      if (r) fresh.push(r);
    }
    if (!fresh.length) return;
    // Everyone starts on the server's beat so files line up; late additions start immediately.
    if (startAtServer) {
      const wait = this.signaling!.clock.toLocal(startAtServer) - localNow();
      if (wait > 0) await sleep(wait);
    }
    // All at once: one source's slow encoder start-up must never delay another's first sample.
    await Promise.all(
      fresh.map(async (r) => {
        try {
          await withTimeout(`${r.source.label} recorder start`, r.recorder.start(), 8000);
          r.startedServerMs = this.signaling!.clock.serverNow();
          this.lastRecorderStart = localNow();
          this.journal?.updateFile(r.localName, { startedServerMs: r.startedServerMs });
          if (!startAtServer) this.logEvent('source-added', `${r.source.label} started recording mid-take`);
          if (r.recorder.ext === 'mp4') this.watchFirstFrame(r);
        } catch (e) {
          r.recorder.markFailed((e as Error).message);
          this.warn(`Couldn’t start recording ${r.source.label}: ${(e as Error).message}`);
          // Close it out so the server shows the file as failed rather than "recording" for ever.
          await this.stopRecorder(r, 'error');
        }
      }),
    );
    this.mesh?.setLocalCap(this.liveCap());
    this.changed();
  }

  private async createRecorder(source: CapturedSource, takeId: string): Promise<ActiveRecorder | null> {
    const hw = this.hardware!;
    const isVideo = source.kind === 'camera' || source.kind === 'screen';
    const ext = isVideo ? 'mp4' : 'wav';
    const localName = `take-${takeId.slice(0, 8)}-${source.kind}-${uid()}.${ext}`;
    const video = this.sources.videoFor(source);
    if (video) this.sources.hold(source.id);
    // The "me" camera carries a scratch copy of the mic, handy for quick edits and A/V sync.
    const mic = this.sources.primaryMic;
    const embedAudio = source.kind === 'camera' && source.use.live && mic ? mic.track : undefined;
    const micSource = embedAudio ? mic : undefined;
    const recorder = new SourceRecorder({
      source,
      fileName: localName,
      storage: this.storage,
      clock: this.signaling!.clock,
      videoCodec: hw.videoCodec,
      videoLatencyMode: hw.videoLatencyMode,
      audioCodec: hw.audioCodec,
      video,
      embedAudio,
      isHardMuted: () => source.hardMuted,
      isEmbedHardMuted: () => !!micSource?.hardMuted,
      wavBits: this.sources.processing === 'studio' ? 24 : 16,
      takeStartServerMs: this.recording?.startAt,
      onWrite: (pos, len) => this.uploader.noteWrite(localName, pos, len),
    });
    recorder.frameDivisor = frameDivisorFor(source.kind, this.loadLevel);
    const active: ActiveRecorder = { recorder, source, localName, trackId: null, startedServerMs: 0 };
    recorder.onError = (e) => void this.recoverRecorder(active, e);
    try {
      await recorder.prepare();
    } catch (e) {
      this.warn(`Can’t record ${source.label}: ${(e as Error).message}`);
      return null;
    }
    // In the journal before the first byte is recorded: whatever happens next, this file can be found and placed.
    await this.journal?.addFile({
      localName,
      sourceId: source.id,
      kind: source.kind,
      label: source.label,
      device: source.track.label || undefined,
      ext,
      startedServerMs: null,
    });
    this.recorders.set(source.id, active);
    void this.registerTrack(active, takeId);
    return active;
  }

  /** Server registration can lag (or retry through an outage); recording never waits for it. */
  private async registerTrack(r: ActiveRecorder, takeId: string) {
    const take = this.recording;
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await api.registerTrack(this.key, {
          takeId,
          participantId: this.participantId,
          participantName: this.name,
          sourceId: r.source.id,
          sourceKind: r.source.kind,
          label: r.source.label,
          ext: r.recorder.ext,
          mime: r.recorder.ext === 'wav' ? 'audio/wav' : 'video/mp4',
          clientFileId: r.localName,
        });
        r.trackId = res.trackId;
        this.journal?.updateFile(r.localName, { trackId: res.trackId });
        this.uploader.add({
          trackId: res.trackId,
          uploadToken: res.uploadToken,
          uploadUrl: res.uploadUrl,
          keepLocal: res.keepLocal,
          localName: r.localName,
          studioId: this.studioId,
          takeId,
          takeNumber: take?.takeNumber ?? 0,
          label: r.source.label,
          kind: r.source.kind,
          participantName: this.name,
        });
        if (r.recorder.state === 'done' || r.recorder.state === 'error') await this.finishUpload(r, 'stopped');
        return;
      } catch (e) {
        if ((e as any).status === 403 || (e as any).status === 404 || (e as any).status === 429) {
          this.warn(`Server rejected ${r.source.label}: ${(e as Error).message}. The local copy is kept.`);
          return;
        }
        await sleep(Math.min(30_000, 1000 * 2 ** attempt));
      }
    }
  }

  private async stopRecorder(r: ActiveRecorder, reason: 'stopped' | 'source-ended' | 'error' | 'left') {
    if (this.recorders.get(r.source.id) === r) {
      this.recorders.delete(r.source.id);
      this.sources.release(r.source.id);
    }
    this.progress.delete(r.recorder);
    try {
      await r.recorder.stop();
    } catch (e) {
      console.error('[takehouse] stopping recorder failed', r.source.label, e);
    }
    if (r.recorder.audioGapSeconds >= 0.1) {
      this.logEvent(
        'warning',
        `${r.source.label}: ${r.recorder.audioGapSeconds.toFixed(1)} s of audio never arrived (busy computer); silence was written in its place so the file stays in time`,
        r.trackId ?? undefined,
      );
    }
    this.journal?.updateFile(r.localName, {
      state: r.recorder.state === 'error' ? 'error' : 'done',
      endedReason: reason,
      size: r.recorder.durableBytes,
      finish: r.recorder.finishInfo(reason),
      draft: r.recorder.recoveryDraft() ?? undefined,
    });
    if (reason !== 'stopped' || this.recording) {
      this.logEvent(reason === 'source-ended' ? 'source-removed' : 'track-ended', `${r.source.label} (${reason})`, r.trackId ?? undefined);
    }
    // Always tell the server how the file ended, or it shows "recording" for ever.
    await this.finishUpload(r, reason).catch((e) => console.error('[takehouse] finishing upload failed', r.source.label, e));
  }

  private async finishUpload(r: ActiveRecorder, reason: 'stopped' | 'source-ended' | 'error' | 'left') {
    if (!r.trackId) return; // registerTrack() will call back here once it succeeds
    const known = r.recorder.durableBytes;
    const size = await Promise.race([this.storage.size(r.localName).catch(() => known), sleep(5000).then(() => known)]);
    this.uploader.finish(r.localName, size, r.recorder.finishInfo(reason));
  }

  /** A video recorder that runs but never encodes a frame would otherwise leave an empty file unnoticed. */
  private watchFirstFrame(r: ActiveRecorder) {
    setTimeout(() => {
      if (this.recorders.get(r.source.id) !== r || r.recorder.videoFrames > 0) return;
      const msg = `${r.source.label} isn’t producing any video, so its recording will be empty. Try reselecting the camera, or use Chrome.`;
      this.warn(msg);
      this.logEvent('warning', msg, r.trackId ?? undefined);
    }, 6000);
  }

  /**
   * An encoder crash shouldn't end the take: close what we have and open a new file for the same source. A
   * failure that keeps coming back (a setting this browser can't encode) would otherwise open file after file,
   * so retries wait longer each time, and after three failures in a minute the source is given up on until
   * the next take, and the person and the host are told.
   */
  private async recoverRecorder(r: ActiveRecorder, e: Error) {
    const id = r.source.id;
    const now = localNow();
    const recent = [...(this.failures.get(id) ?? []).filter((t) => now - t < 60_000), now];
    this.failures.set(id, recent);
    this.recorders.delete(id);
    this.sources.release(id);
    const giveUp = recent.length >= MAX_RECORDER_FAILURES;
    if (giveUp) {
      this.givenUp.add(id);
      const msg = `${r.source.label} can’t be recorded in this browser (${e.message}). It’s still on the call; the rest of the take carries on recording.`;
      this.warn(msg);
      this.logEvent('warning', `Stopped recording ${r.source.label} after ${recent.length} failures: ${e.message}`, r.trackId ?? undefined);
    } else {
      this.warn(`${r.source.label}: recorder error (${e.message}). Started a new file to continue.`);
      this.logEvent('warning', `Recorder error on ${r.source.label}: ${e.message}`, r.trackId ?? undefined);
    }
    // Open the replacement file first, then close the broken one: closing can take seconds, and every
    // second of that would otherwise be lost from the recording. A repeat failure waits a little first.
    const wait = RECORDER_RETRY_WAIT_MS[recent.length - 1] ?? 0;
    const restart =
      !giveUp && this.recording && !r.source.ended && r.source.use.record
        ? sleep(wait).then(() => (this.recording ? this.syncRecorders() : undefined))
        : Promise.resolve();
    await Promise.all([this.stopRecorder(r, 'error'), restart]);
  }

  private async onSourceEnded(source: CapturedSource) {
    const r = this.recorders.get(source.id);
    if (r) await this.stopRecorder(r, 'source-ended');
  }

  private async endTake(reason: 'stopped' | 'left') {
    const all = [...this.recorders.values()];
    if (this.recording) this.lastTake = { takeId: this.recording.takeId, takeNumber: this.recording.takeNumber, localOnly: this.recording.localOnly };
    this.lastTakeSave = null;
    this.recording = null;
    this.governor.setRecording(false);
    this.changed();
    await this.syncing;
    await Promise.all(all.map((r) => this.stopRecorder(r, reason)));
    const journal = this.journal;
    this.journal = null;
    await journal?.close().catch(() => {});
    try {
      await this.wakeLock?.release();
    } catch {}
    this.wakeLock = null;
    await this.refreshStorage();
    this.changed();
    if (saveFolder().handle && this.lastTake) void this.saveLastTake(false);
  }

  /**
   * Copies this computer's files of the last take into the save folder. Automatic at the end of a take when the
   * browser still allows writing there; otherwise the end-of-take note offers it (`ask`: from a click, so the
   * browser may ask for permission).
   */
  async saveLastTake(ask: boolean) {
    const take = this.lastTake;
    const folder = saveFolder();
    if (!take || !folder.handle) return;
    const root = ask ? ((await folder.allow()) ? folder.handle : null) : await folder.ready();
    if (!root) {
      this.lastTakeSave = { state: 'needs-permission' };
      this.changed();
      return;
    }
    this.lastTakeSave = { state: 'saving' };
    this.changed();
    try {
      // The timings go to the server first, so the saved timeline and manifest have them.
      for (let i = 0; i < 20 && this.uploader.jobs.some((j) => j.takeId === take.takeId && j.finish && !j.finishSent); i++) await sleep(500);
      const local = (await listLocalTakes(this.storage)).find((t) => t.journal.takeId === take.takeId && t.journal.participantId === this.participantId);
      if (!local) throw new Error('its files aren’t on this computer');
      const name = await saveTakeToFolder(this.storage, local, { root });
      this.lastTakeSave = { state: 'saved', folder: `${root.name}/${name}` };
    } catch (e) {
      this.lastTakeSave = { state: 'failed', error: (e as Error).message };
    }
    this.changed();
  }

  private logEvent(type: TimelineEvent['type'], detail?: string, trackId?: string) {
    if (!this.recording || !this.signaling) return;
    const event = { takeId: this.recording.takeId, t: this.signaling.clock.serverNow(), type, detail, trackId } as TimelineEvent;
    this.journal?.addEvent({ t: event.t, type, detail, trackId });
    if (this.connected) this.signaling.send({ type: 'event', event });
    else this.outbox.push(event);
  }

  private warn(msg: string) {
    console.warn(`[takehouse] shown to user: ${msg}`);
    // Send diagnostics straight away (the regular send is every 15 s), but not for every warning in a burst.
    if (localNow() - this.lastDiagFlush > 5000) {
      this.lastDiagFlush = localNow();
      this.flushDiag();
    }
    this.warnings = [...this.warnings.filter((w) => w !== msg), msg].slice(-5);
    this.changed();
  }

  dismissWarning(msg: string) {
    this.warnings = this.warnings.filter((w) => w !== msg);
    this.changed();
  }

  // --- checks / instant replay --------------------------------------------------

  /**
   * Replays the last few seconds of everything being recorded, read back from disk.
   * When not recording, runs a short throwaway recording first (a pre-flight test).
   */
  async runCheck(reason: CheckRun['reason'] | 'auto', opts: { checkId?: string; requestedBy?: string } = {}) {
    if (this.check?.status === 'capturing') return;
    const recordingNow = this.recording && this.recorders.size > 0;
    const run: CheckRun = {
      id: uid(),
      reason: reason === 'auto' ? (recordingNow ? 'self' : 'preflight') : reason,
      requestedBy: opts.requestedBy,
      checkId: opts.checkId,
      status: 'capturing',
      clips: [],
    };
    this.check = run;
    this.changed();
    try {
      let clips: TailClip[];
      if (recordingNow) {
        clips = await this.tailClips([...this.recorders.values()].map((r) => r.recorder));
        // A just-started take may not have a finished fragment yet; wait a moment and retry.
        if (clips.length < this.recorders.size) {
          await sleep(2500);
          clips = await this.tailClips([...this.recorders.values()].map((r) => r.recorder));
        }
      } else {
        clips = await this.preflight(run);
        if (run.struggled != null) this.sources.learnFromTest(run.struggled);
      }
      run.clips = clips;
      run.status = clips.length ? 'ready' : 'failed';
      if (!clips.length) run.message = 'Nothing is set to record. Turn on “Record” for at least one source.';
    } catch (e) {
      run.status = 'failed';
      run.message = (e as Error).message;
    }
    this.changed();
    return run;
  }

  private async tailClips(recorders: SourceRecorder[]) {
    const clips = await Promise.all(recorders.map((r) => r.tailClip(5).catch(() => null)));
    return clips.filter((c): c is TailClip => !!c);
  }

  private async preflight(run: CheckRun): Promise<TailClip[]> {
    const hw = this.hardware;
    if (!hw) throw new Error('Still starting up…');
    // In the green room there's no server clock yet; a local one is fine for a throwaway test.
    const clock = this.signaling?.clock ?? new ServerClock();
    const recs: { recorder: SourceRecorder; name: string }[] = [];
    for (const source of this.sources.active.filter((s) => s.use.record)) {
      const isVideo = source.kind === 'camera' || source.kind === 'screen';
      const name = `test-${uid()}.${isVideo ? 'mp4' : 'wav'}`;
      const recorder = new SourceRecorder({
        source,
        fileName: name,
        storage: this.storage,
        clock,
        videoCodec: hw.videoCodec,
        videoLatencyMode: hw.videoLatencyMode,
        audioCodec: hw.audioCodec,
        video: this.sources.videoFor(source),
        embedAudio: source.kind === 'camera' && source.use.live ? this.sources.primaryMic?.track : undefined,
        isHardMuted: () => source.hardMuted,
        isEmbedHardMuted: () => !!this.sources.primaryMic?.hardMuted,
        wavBits: this.sources.processing === 'studio' ? 24 : 16,
        onWrite: () => {},
      });
      await recorder.prepare();
      recs.push({ recorder, name });
    }
    if (!recs.length) return [];
    try {
      await Promise.all(recs.map((r) => r.recorder.start()));
      // How far the video encoders fall behind while everything records: the honest test of the plan.
      const dropsBefore = pipelineStats.droppedVideoFrames;
      const backlog: number[] = [];
      for (let s = 6; s > 0; s--) {
        run.countdown = s;
        this.changed();
        for (let i = 0; i < 5; i++) {
          await sleep(200);
          // The first second is warm-up (the encoders' first frames always queue briefly).
          if (s < 6) backlog.push(Math.max(0, ...recs.filter((r) => r.recorder.ext === 'mp4').map((r) => r.recorder.encoderBacklog)));
        }
      }
      run.countdown = 0;
      if (backlog.length) {
        const sorted = [...backlog].sort((a, b) => a - b);
        const typical = sorted[Math.floor(sorted.length * 0.8)];
        const dropped = pipelineStats.droppedVideoFrames - dropsBefore;
        // Mediabunny drops frames at a backlog of 8; most of the time at 4+ means the encoder isn't keeping up.
        run.struggled = typical >= 4 || dropped > 6;
        console.info(`[takehouse] test encoder backlog: typical ${typical}, peak ${sorted.at(-1)}, pipeline drops ${dropped}`);
      }
      // Finalize first so the last fragment is on disk, then read back exactly what was stored.
      await Promise.all(recs.map((r) => r.recorder.stop()));
      return await this.tailClips(recs.map((r) => r.recorder));
    } finally {
      for (const r of recs) {
        if (r.recorder.state === 'recording') await r.recorder.stop().catch(() => {});
        setTimeout(() => void this.storage.delete(r.name), 1000);
      }
    }
  }

  /** Send the replayed clips to the host, with this machine's automatic verdicts. */
  async shareCheck(run: CheckRun, verdicts: Map<string, CheckVerdict>) {
    if (!run.checkId) return;
    for (const clip of run.clips) {
      const verdict = verdicts.get(clip.sourceId) ?? { ok: true, summary: '' };
      await api
        .uploadCheckClip(
          run.checkId,
          this.key,
          { participantId: this.participantId, participantName: this.name, label: clip.label, kind: clip.kind, mime: clip.mime },
          verdict,
          new Blob([clip.data as Uint8Array<ArrayBuffer>], { type: clip.mime }),
        )
        .catch((e) => this.warn(`Couldn’t send check clip: ${(e as Error).message}`));
    }
  }

  closeCheck() {
    this.check = null;
    this.changed();
  }

  // --- status -------------------------------------------------------------------

  async refreshStorage() {
    const est = await storageEstimate();
    this.storageFree = Number.isFinite(est.free) ? est.free : undefined;
    this.changed();
  }

  /** Rough recording bitrate in bytes/s, for "time left on disk". */
  get recordingBytesPerSec() {
    let bps = 0;
    for (const r of this.recorders.values()) {
      bps += r.recorder.format === 'wav' ? 48000 * 2 * (r.recorder.channels ?? 1) * 8 : (r.recorder.o.video?.bitrate ?? 4e6) + 160e3;
    }
    return bps / 8;
  }

  private statusTick = 0;
  private publishStatus() {
    if (++this.statusTick % 15 === 0) void this.refreshStorage();
    const warnings = [...this.warnings];
    if (this.storageFree != null && this.recording) {
      const minutes = this.storageFree / Math.max(1, this.recordingBytesPerSec) / 60;
      if (minutes < 20) warnings.push(`Low disk space: about ${Math.floor(minutes)} min of recording left`);
    }
    if (!this.storage.persistent) warnings.push('Recording to memory only – this browser blocked disk storage');
    if (this.loadLevel > 0) warnings.push(`Busy computer – ${LOAD_TEXT[this.loadLevel].toLowerCase()}`);
    const totals = this.uploader.totals(this.uploader.jobs.filter((j) => j.studioId === this.studioId));
    const firstPeer = [...(this.mesh?.peers.values() ?? [])][0];
    const status: ParticipantStatus = {
      recording: this.recorders.size > 0,
      sources: this.sources.active.map((s) => ({
        id: s.id,
        kind: s.kind,
        label: s.label,
        live: s.use.live && muteState(s) === 'none',
        mute: muteState(s),
        record: s.use.record,
        detail: this.recorders.get(s.id)?.recorder.state,
      })),
      bytesRecorded: totals.total,
      bytesUploaded: totals.uploaded,
      storageFreeBytes: this.storageFree,
      uploadRateBps: this.uploader.currentBps,
      uploadMode: this.governor.mode,
      liveTier: firstPeer?.tier,
      cpuPressure: this.cpuPressure,
      rttMs: this.governor.health.rttMs ?? undefined,
      clockUncertaintyMs: this.signaling ? Math.round(this.signaling.clock.uncertaintyMs) : undefined,
      hardwareTier: this.hardware?.tier,
      warnings,
    };
    this.signaling?.send({ type: 'status', status });
    this.evaluateLoad();
    this.mesh?.setLocalCap(this.liveCap());
    // Keep crash-recovery info fresh for every file being written.
    if (this.statusTick % 2 === 0) {
      for (const r of this.recorders.values()) {
        const draft = r.recorder.recoveryDraft();
        if (draft) {
          this.uploader.setDraft(r.localName, draft);
          this.journal?.updateFile(r.localName, { draft });
        }
      }
    }
  }
}

export type LoadLevel = 0 | 1 | 2 | 3;

const LOAD_TEXT: Record<LoadLevel, string> = {
  0: 'Everything at full quality',
  1: 'Uploads paused and live video reduced',
  2: 'Uploads paused, live video at its lowest and the screen recorded at half frame rate',
  3: 'Uploads paused, live video at its lowest, and the camera and screen recorded at reduced frame rates',
};

/** Recording frame-rate divisor for a source at a protection level. Audio is never reduced. */
export function frameDivisorFor(kind: SourceKind, level: LoadLevel): number {
  if (kind === 'screen') return level >= 3 ? 3 : level >= 2 ? 2 : 1;
  if (kind === 'camera') return level >= 3 ? 2 : 1;
  return 1;
}
